import React, { useState, useEffect } from "react";
import { Form } from "semantic-ui-react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import { Responsible, Country, Industry, Division } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";

const responsibleController = new Responsible();
const countryController     = new Country();
const industryController    = new Industry();
const divisionController    = new Division();

export function ResponsibleForm({ responsible, close, onReload }) {
  const { accessToken } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const [countryOptions,  setCountryOptions]  = useState([]);
  const [industryOptions, setIndustryOptions] = useState([]);
  const [divisionOptions, setDivisionOptions] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [countries, industries, divisions] = await Promise.all([
          countryController.getCountries(accessToken, true),
          industryController.getIndustries(accessToken, true),
          divisionController.getDivisions(accessToken, true),
        ]);
        setCountryOptions(countries.map((c) => ({ key: c._id, value: c._id, text: `${c.code} - ${c.name}` })));
        setIndustryOptions(industries.map((i) => ({ key: i._id, value: i._id, text: `${i.code} - ${i.name}` })));
        setDivisionOptions(divisions.map((d) => ({ key: d._id, value: d._id, text: `${d.code} - ${d.name}` })));
      } catch {}
    })();
  }, []);

  const formik = useFormik({
    initialValues: {
      country:  (responsible?.country?._id  || responsible?.country)  ?? "",
      industry: (responsible?.industry?._id || responsible?.industry) ?? "",
      division: (responsible?.division?._id || responsible?.division) ?? "",
      position: responsible?.position || "",
    },
    validationSchema: Yup.object({
      country:  Yup.string().required("El país es requerido"),
      industry: Yup.string().required("La industria es requerida"),
      division: Yup.string().required("La división es requerida"),
      position: Yup.string().required("El puesto es requerido"),
    }),
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (responsible) {
          await responsibleController.updateResponsible(accessToken, responsible._id, values);
          toast.success(t("updated"));
        } else {
          await responsibleController.createResponsible(accessToken, values);
          toast.success(t("created"));
        }
        onReload();
        close();
      } catch (err) {
        toast.error(err?.msg || t("error"));
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Form onSubmit={formik.handleSubmit} style={{ padding: "0.5rem" }}>
      <ToastContainer position="top-center" autoClose={800} hideProgressBar />
      <Form.Select
        label={<label>{t("country")} <span style={{ color: "#C0392B" }}>*</span></label>}
        name="country"
        value={formik.values.country}
        options={countryOptions}
        onChange={(_, { value }) => formik.setFieldValue("country", value)}
        error={formik.errors.country}
        placeholder={t("country")}
        search
      />
      <Form.Select
        label={<label>{t("industry")} <span style={{ color: "#C0392B" }}>*</span></label>}
        name="industry"
        value={formik.values.industry}
        options={industryOptions}
        onChange={(_, { value }) => formik.setFieldValue("industry", value)}
        error={formik.errors.industry}
        placeholder={t("industry")}
        search
      />
      <Form.Select
        label={<label>{t("division")} <span style={{ color: "#C0392B" }}>*</span></label>}
        name="division"
        value={formik.values.division}
        options={divisionOptions}
        onChange={(_, { value }) => formik.setFieldValue("division", value)}
        error={formik.errors.division}
        placeholder={t("division")}
        search
      />
      <Form.Input
        label={<label>{t("position")} <span style={{ color: "#C0392B" }}>*</span></label>}
        name="position"
        value={formik.values.position}
        onChange={formik.handleChange}
        error={formik.errors.position}
        placeholder="Ej: Gerente de Tienda"
      />
      <div style={{ display: "flex", justifyContent: "center", gap: "14px", marginTop: "2rem" }}>
        <button type="submit" disabled={formik.isSubmitting} style={{ background: "#4A7FA5", color: "#fff", border: "none", borderRadius: "20px", padding: "10px 30px", fontSize: "14px", fontWeight: 600, fontFamily: "'Archivo', sans-serif", cursor: "pointer", opacity: formik.isSubmitting ? 0.55 : 1 }}>
          {formik.isSubmitting ? "…" : (responsible ? t("update") : t("save"))}
        </button>
        <button type="button" onClick={close} style={{ background: "#9BAFC4", color: "#fff", border: "none", borderRadius: "20px", padding: "10px 30px", fontSize: "14px", fontWeight: 600, fontFamily: "'Archivo', sans-serif", cursor: "pointer" }}>
          {t("cancel")}
        </button>
      </div>
    </Form>
  );
}
